import React, { ReactNode } from 'react'

import { Accordion, AccordionItem, AccordionItemProps } from '../../components/accordion/accordion.js'
import { Button, ButtonProps } from '../../components/button/button.js'
import { AnimatedHeightContainer, CartGrid, CartGridProps, CartItem } from '../../components/cart/cart.js'
import { GridColumn } from '../../components/grid/grid-column.js'
import { Grid } from '../../components/grid/grid.js'
import { useCurrentBreakPoint } from '../../components/helper/hooks/use-current-breakpoint.js'
import { Modal, ModalHeader, ModalProps } from '../../components/modal/modal.js'
import { Product, ProductProps } from '../../components/product/product.js'
import { Separator } from '../../components/separator/separator.js'

export interface CartProps {
    alwaysFixed?: boolean
    cartItems: CartGridProps[]
    cartAdditionItemsClass?: string
    cartAdditionItems: Array<CartGridProps | ProductProps>
    cartFooter: {
        button: ButtonProps
        accordion?: Omit<AccordionItemProps, 'id'>
    }
    price?: string
    specialPrice?: string
    subPrice?: ReactNode
    fraudModal?: FraudModalProps
    isEmpty?: boolean
    emptyText?: string
    noticeText?: string
}

interface FraudModalProps {
    title: string
    modal: ModalProps
}

const isProduct = (item: CartGridProps | ProductProps): item is ProductProps => {
    return (item as ProductProps).sku !== undefined
}

const renderMobileCartContent = (props: CartProps): JSX.Element => {
    return (
        <>
            <CartItem>
                {props.isEmpty && props.emptyText && <p className="h-text-center text-small">{props.emptyText}</p>}
                {!props.isEmpty && props.emptyText && props.cartFooter.accordion && (
                    <Accordion>
                        <AccordionItem id="cart-accordion" {...props.cartFooter.accordion}>
                            {props.cartItems.map((cartItem: CartGridProps, index: number) => {
                                const key = `cart-item-${index}-mobile`
                                const isLast: boolean = props.cartItems.length === index + 1

                                return (
                                    <div key={key}>
                                        <CartGrid {...cartItem} />
                                        {!cartItem.hideSeparator && !isLast && <Separator />}
                                    </div>
                                )
                            })}
                        </AccordionItem>
                    </Accordion>
                )}
            </CartItem>
            <CartItem>
                {props.alwaysFixed && (
                    <Grid verticalGutter="none">
                        <GridColumn width={['xs-12']}>
                            <Button {...props.cartFooter.button} isBlock={true} variant="tertiary" />
                        </GridColumn>
                    </Grid>
                )}
                {!props.alwaysFixed && (
                    <Grid verticalGutter="none">
                        {props.alwaysFixed && (
                            <GridColumn width={['xs-12']}>
                                <Button {...props.cartFooter.button} isBlock={true} variant="tertiary" />
                            </GridColumn>
                        )}
                        <GridColumn width={['xs-2', 'sm-8']}>
                            {props.price && <p className="text-small text-price">{props.price}</p>}
                            {props.specialPrice && (
                                <p className="text-small text-price is-special">{props.specialPrice}</p>
                            )}
                            {props.subPrice}
                        </GridColumn>
                        <GridColumn width={['xs-2', 'sm-4']}>
                            <Button {...props.cartFooter.button} isBlock={true} variant="tertiary" />
                        </GridColumn>
                        {props.noticeText && (
                            <GridColumn width={['xs-2', 'sm-4']}>
                                <p className="text-small">{props.noticeText}</p>
                            </GridColumn>
                        )}
                    </Grid>
                )}
            </CartItem>
        </>
    )
}

const renderCartContent = (props: CartProps): JSX.Element => {
    return (
        <CartItem>
            {props.cartItems.map((cartItem: CartGridProps, index: number) => {
                const key = `cart-item-${index}-desktop`

                return (
                    <div key={key}>
                        <CartGrid {...cartItem} />
                        {!cartItem.hideSeparator && <Separator />}
                    </div>
                )
            })}
            <div className="text">
                {props.isEmpty && props.emptyText && <p className="h-text-center text-small">{props.emptyText}</p>}
                {!props.isEmpty && props.emptyText && props.cartAdditionItems && props.cartFooter.accordion && (
                    <Accordion className={props.cartAdditionItemsClass}>
                        <AccordionItem id="cart-accordion" {...props.cartFooter.accordion}>
                            {props.cartAdditionItems.map((cartItem, index: number) => {
                                const key = `cart-additional-item-${index}-desktop`
                                const isLast: boolean = props.cartAdditionItems.length === index + 1
                                const hideSeparator = isProduct(cartItem) ? false : cartItem.hideSeparator
                                return (
                                    <div key={key}>
                                        {isProduct(cartItem) ? <Product {...cartItem} /> : <CartGrid {...cartItem} />}
                                        {!hideSeparator && !isLast && <Separator />}
                                    </div>
                                )
                            })}
                        </AccordionItem>
                    </Accordion>
                )}
                <Button {...props.cartFooter.button} isBlock={true} variant="tertiary" />
                {props.noticeText && <p className="text-small">{props.noticeText}</p>}
            </div>
        </CartItem>
    )
}

const fraudProtectionModal = (props: FraudModalProps): JSX.Element => {
    const renderModalComponents = (): JSX.Element => <ModalHeader>{props.title}</ModalHeader>

    return (
        <Modal {...props.modal} className="is-please-wait">
            {renderModalComponents()}
        </Modal>
    )
}

export const Cart = (props: CartProps): JSX.Element => {
    const currentBreak = useCurrentBreakPoint()
    const isMobile: boolean | null = currentBreak && ['xs', 'sm'].includes(currentBreak)

    const BEM = () => {
        const classes = []

        if (props.alwaysFixed) {
            classes.push('cart--always-fixed')
        }

        return classes.join(' ')
    }

    return (
        <AnimatedHeightContainer className={BEM()}>
            {isMobile || props.alwaysFixed ? renderMobileCartContent(props) : renderCartContent(props)}
            {props.fraudModal && fraudProtectionModal(props.fraudModal)}
        </AnimatedHeightContainer>
    )
}
